import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { UpdateComissionReceivableTransaction } from "../../../../Service/_index";
import { ToastError, ToastSuccess } from "../../../../UiComponents/Toaster/Toast";

/**
 * Edits one already saved entry of the Transaction History.
 *
 * Once a policy is fully received there is no pending amount left, so a wrong entry
 * can no longer be corrected by adding another one - it has to be edited here. The
 * backend moves the policy's Received/Pending amounts by the difference between the
 * old and the new amount, so the same limit applies here: an entry can grow only by
 * what is still pending.
 */
// Amounts are compared as rupees, not as raw floats: 22847.82 + 0.01 is
// 22847.829999999998 in binary floating point, so an amount that looks exactly equal
// to the max would otherwise be rejected as "more than" it.
const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const EditTransactionDialog = ({
  open,
  transaction,
  pendingAmount,
  onClose,
  onSaved,
}) => {
  const [values, setValues] = useState({
    entryDate: null,
    bankName: "",
    remarks: "",
    receivedAmount: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const oldAmount = Number(transaction?.receivedAmount || 0);
  const maxAmount = round2(oldAmount + Number(pendingAmount || 0));

  useEffect(() => {
    if (!open || !transaction) {
      return;
    }
    setValues({
      entryDate: transaction.entryDate ? dayjs(transaction.entryDate) : dayjs(),
      bankName: transaction.bankName || "",
      remarks: transaction.remarks || "",
      receivedAmount: transaction.receivedAmount ?? "",
    });
    setError("");
  }, [open, transaction]);

  const handleChange = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
    setError("");
  };

  const handleSave = () => {
    const amount = Number(values.receivedAmount);
    if (!values.receivedAmount || isNaN(amount)) {
      setError("Enter Amount");
      return;
    }
    if (amount <= 0) {
      setError("Amount must be greater than 0");
      return;
    }
    // Same rule the backend enforces - the entry can only take what is still pending
    if (round2(amount) > maxAmount) {
      setError(`Amount cannot be more than ${maxAmount.toFixed(2)}`);
      return;
    }

    setSaving(true);
    UpdateComissionReceivableTransaction(transaction._id, {
      receivedAmount: amount,
      entryDate: values.entryDate ? values.entryDate.toDate() : undefined,
      bankName: values.bankName,
      remarks: values.remarks,
    })
      .then((res) => {
        ToastSuccess("Transaction Updated Successfully");
        onSaved(res?.data);
        onClose();
      })
      .catch((err) => {
        ToastError(
          err?.response?.data?.message || "Failed to update the transaction",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>Edit Transaction</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container columnSpacing={4} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: "12px", mb: 1 }}>Entry Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                className="w-100 Date_Picker"
                value={values.entryDate}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, entryDate: e }))
                }
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: "12px", mb: 1 }}>
              {`Amount (Max ${maxAmount.toFixed(2)})`}
            </Typography>
            <input
              className="InputFiled"
              name="receivedAmount"
              value={values.receivedAmount}
              onChange={handleChange("receivedAmount")}
            />
            <div className="errorMessage">{error || " "}</div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: "12px", mb: 1 }}>Bank</Typography>
            <input
              className="InputFiled"
              name="bankName"
              value={values.bankName}
              style={{ textTransform: "uppercase" }}
              onChange={handleChange("bankName")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: "12px", mb: 1 }}>Remark</Typography>
            <input
              className="InputFiled"
              name="remarks"
              value={values.remarks}
              style={{ textTransform: "uppercase" }}
              onChange={handleChange("remarks")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button className="Dialog_Cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          className="Dialog_Save_Exit"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTransactionDialog;
